import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ConfirmContext = createContext();

let globalConfirm = null;

export const useConfirm = () => useContext(ConfirmContext);

// API impérative — utilisable sans hook
export const confirm = (messageOrOptions, options = {}) => {
  let message = messageOrOptions;
  if (typeof messageOrOptions === 'object' && messageOrOptions !== null) {
    message = messageOrOptions.message || '';
    options = { ...messageOrOptions, ...options };
    delete options.message;
  }
  if (globalConfirm) return globalConfirm(message, options);
  return Promise.resolve(window.confirm(message)); // fallback
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setState({ message, options, resolve });
    });
  }, []);

  useEffect(() => {
    globalConfirm = showConfirm;
    return () => { globalConfirm = null; };
  }, [showConfirm]);

  const handleConfirm = () => {
    state?.resolve(true);
    setState(null);
  };

  const handleCancel = () => {
    state?.resolve(false);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={showConfirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" onClick={handleCancel}>
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
          
          {/* Dialog */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-[scaleIn_0.2s_ease-out]"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-2">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-xl ${state.options.type === 'danger' ? 'bg-red-100' : 'bg-amber-100'}`}>
                  {state.options.type === 'danger' ? (
                    <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  {state.options.title || 'Confirmation'}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 pb-4">
              <p className="text-gray-600 whitespace-pre-line leading-relaxed">{state.message}</p>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
              >
                {state.options.cancelText || 'Annuler'}
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 py-2.5 px-4 rounded-xl transition font-medium text-white ${
                  state.options.type === 'danger'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {state.options.confirmText || 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </ConfirmContext.Provider>
  );
}
